import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, PawPrint, Trash2, RefreshCw, Phone, Calendar, MessageCircle } from "lucide-react";
import { verifyAdmin, listAppointments, updateAppointmentStatus, deleteAppointment } from "../lib/api";
import { SITE } from "../lib/site";

const STORAGE_KEY = "tsp_admin_pwd";
const safeStorage = typeof window !== "undefined" ? window.sessionStorage : null;

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendente", className: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "confirmed", label: "Confirmado", className: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "done", label: "Concluído", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "cancelled", label: "Cancelado", className: "bg-rose-100 text-rose-800 border-rose-200" },
];

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

export default function Admin() {
  const [pwd, setPwd] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const refresh = useCallback(async (pass) => {
    setLoading(true);
    try {
      const data = await listAppointments(pass);
      setItems(data);
    } catch (e) {
      toast.error("Falha ao carregar.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = safeStorage?.getItem(STORAGE_KEY);
    if (!saved) return;
    verifyAdmin(saved)
      .then(() => {
        setPwd(saved);
        setAuthed(true);
        refresh(saved);
      })
      .catch(() => safeStorage?.removeItem(STORAGE_KEY));
  }, [refresh]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyAdmin(pwd);
      safeStorage?.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      toast.success("Bem-vinda, Tia Sandra!");
      refresh(pwd);
    } catch {
      toast.error("Senha incorreta.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    safeStorage?.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPwd("");
    setItems([]);
  };

  const onStatus = async (id, status) => {
    try {
      const updated = await updateAppointmentStatus(id, status, pwd);
      setItems((arr) => arr.map((x) => (x.id === id ? updated : x)));
      toast.success("Status atualizado.");
    } catch {
      toast.error("Erro ao atualizar.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este agendamento?")) return;
    try {
      await deleteAppointment(id, pwd);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Excluído.");
    } catch {
      toast.error("Erro ao excluir.");
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-subtle via-white to-sky-soft/50 flex items-center justify-center px-4">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white rounded-[32px] shadow-2xl shadow-royal/10 p-8 border border-slate-100"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-royal flex items-center justify-center text-gold">
              <PawPrint className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading text-xl text-royal font-semibold">Painel Admin</div>
              <div className="text-xs uppercase tracking-[0.18em] text-gold">Tia Sandra Pet Care</div>
            </div>
          </div>
          <Label htmlFor="pwd" className="text-royal font-semibold text-sm">Senha de administrador</Label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="pwd"
              data-testid="admin-pwd"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Sua senha"
              className="pl-10 h-12 rounded-xl bg-slate-50 border-slate-200 focus-visible:ring-royal focus-visible:border-royal"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-gold hover:bg-gold-hover text-royal font-bold py-6 shadow-lg"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Entrando…</> : "Entrar"}
          </Button>
          <a href="/" className="block text-center mt-4 text-xs text-slate-500 hover:text-royal">← Voltar ao site</a>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-sky-subtle/40">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30 backdrop-blur">
        <div className="container-pad h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-royal text-gold flex items-center justify-center">
              <PawPrint className="w-4 h-4" />
            </div>
            <div>
              <div className="text-royal font-heading font-semibold leading-none">Painel</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Tia Sandra Pet Care</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="admin-refresh" onClick={() => refresh(pwd)} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
            </Button>
            <Button variant="outline" size="sm" data-testid="admin-logout" onClick={logout} className="rounded-full">
              <LogOut className="w-4 h-4 mr-1" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container-pad py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl text-royal font-semibold">Agendamentos</h1>
            <p className="text-slate-500 text-sm mt-1">{items.length} no total</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter" className="w-[200px] rounded-full bg-white border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-[28px] p-12 text-center text-slate-500 border border-slate-100">
            Nenhum agendamento encontrado.
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((a) => {
              const meta = statusMeta(a.status);
              const date = new Date(a.created_at).toLocaleString("pt-BR");
              const cleanPhone = a.phone.replace(/\D/g, "");
              const waMsg = `Olá ${a.name}! Sobre o agendamento de ${a.service} para o(a) ${a.pet_name}…`;
              return (
                <div
                  key={a.id}
                  data-testid={`appt-${a.id}`}
                  className="bg-white rounded-[24px] p-5 sm:p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Cliente</div>
                        <div className="font-heading text-royal font-semibold">{a.name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Pet</div>
                        <div className="text-slate-700">{a.pet_name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Serviço</div>
                        <div className="text-slate-700">{a.service}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Recebido em</div>
                        <div className="text-slate-700 text-sm flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${meta.className} border rounded-full px-3 py-1 text-xs`}>{meta.label}</Badge>
                      <Select value={a.status} onValueChange={(v) => onStatus(a.id, v)}>
                        <SelectTrigger className="w-[140px] h-9 rounded-full bg-slate-50 border-slate-200 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <a
                        href={`https://wa.me/${cleanPhone.length > 10 ? cleanPhone : "55" + cleanPhone}?text=${encodeURIComponent(waMsg)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600"
                      >
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                      <button
                        data-testid={`delete-${a.id}`}
                        onClick={() => onDelete(a.id)}
                        className="h-9 w-9 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center"
                        aria-label="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-royal" />{a.phone}</span>
                    {a.message && <span className="italic">"{a.message}"</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
